import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MerchantStateStore } from '../merchant/merchant.state.store';
import { Merchant, State } from '../merchant/merchant.state.store';
import { take, catchError, filter, switchMap, map } from 'rxjs/operators';
import { Subscription, Observable } from 'rxjs';
import { AuthService } from '../user/auth.service';
import { Location } from '@angular/common';
import { getStateMessage, MerchantEvent, MerchantState } from '../shared/machines/merchant.state.machine';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../environments/environment';
import { RegistryFirebaseService } from '../registry/registry-firebase.service';
import { Settings } from '../settings/settings';
import { Registry } from '../registry/registry';

// Recreate timer with interval for compatibility
function timer(delay: number, period: number): Observable<number> {
  return new Observable<number>(subscriber => {
    const start = Date.now();
    let timeoutId: any;

    const scheduleNext = (count: number) => {
      timeoutId = setTimeout(() => {
        subscriber.next(count);
        scheduleNext(count + 1);
      }, period);
    };

    // Initial delay
    timeoutId = setTimeout(() => {
      subscriber.next(0);
      scheduleNext(1);
    }, delay);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
    };
  });
}

@Component({
  selector: 'app-credit-card-setup',
  templateUrl: './credit-card-setup.component.html',
  styleUrls: ['./credit-card-setup.component.scss']
})
export class CreditCardSetupComponent implements OnInit, OnDestroy {
  private subscription: Subscription | null = null;
  isLoading = false;
  error = false;
  hasMerchant = false;
  merchantCountry = '';
  merchantCurrency = '';
  stateMessage: string = '';
  MerchantState = MerchantState;
  isVerificationLoading = false;
  isVerificationReady = false;
  merchant: Merchant;
  showModal = true;
  private personaClient: any;
  private use_prod_persona = true; // Toggle for production Persona environment

  private get personaConfig() {
    return this.use_prod_persona ? {
      templateId: 'itmpl_LkXEBoPJCiuESvnfNDrwciDoMrVe', // Production template ID
      environmentId: 'env_NjNzG42nTmyH4P8fTEZa3onEGGM3' // Production environment ID
    } : {
      templateId: 'itmpl_wcRLwn3JN9p5x6TSeVAQd9fSDZj9', // Sandbox template ID
      environmentId: 'env_Lv2intUASo2CfFmLUfC2MbhaHpNe' // Sandbox environment ID
    };
  }

  countryDialCodes = [
    { code: 'US', name: 'United States', dialCode: '+1' },
    { code: 'GB', name: 'United Kingdom', dialCode: '+44' },
    { code: 'AU', name: 'Australia', dialCode: '+61' },
    { code: 'CA', name: 'Canada', dialCode: '+1' },
    { code: 'NZ', name: 'New Zealand', dialCode: '+64' },
    { code: 'SG', name: 'Singapore', dialCode: '+65' },
    { code: 'HK', name: 'Hong Kong', dialCode: '+852' },
    { code: 'AT', name: 'Austria', dialCode: '+43' },
    { code: 'BE', name: 'Belgium', dialCode: '+32' },
    { code: 'EE', name: 'Estonia', dialCode: '+372' },
    { code: 'FI', name: 'Finland', dialCode: '+358' },
    { code: 'FR', name: 'France', dialCode: '+33' },
    { code: 'DE', name: 'Germany', dialCode: '+49' },
    { code: 'GR', name: 'Greece', dialCode: '+30' },
    { code: 'IE', name: 'Ireland', dialCode: '+353' },
    { code: 'IT', name: 'Italy', dialCode: '+39' },
    { code: 'LV', name: 'Latvia', dialCode: '+371' },
    { code: 'LT', name: 'Lithuania', dialCode: '+370' },
    { code: 'LU', name: 'Luxembourg', dialCode: '+352' },
    { code: 'NL', name: 'Netherlands', dialCode: '+31' },
    { code: 'PT', name: 'Portugal', dialCode: '+351' },
    { code: 'SK', name: 'Slovakia', dialCode: '+421' },
    { code: 'SI', name: 'Slovenia', dialCode: '+386' },
    { code: 'ES', name: 'Spain', dialCode: '+34' },
    { code: 'SE', name: 'Sweden', dialCode: '+46' },
    { code: 'CH', name: 'Switzerland', dialCode: '+41' },
    { code: 'DK', name: 'Denmark', dialCode: '+45' },
    { code: 'NO', name: 'Norway', dialCode: '+47' }
  ];

  selectedCountryCode = '';

  get states() {
    return MerchantState;
  }



  get currentState() {
    if (!this.merchantStateStore || !this.merchantStateStore.stateMachine) {
      return MerchantState.INITIAL;
    }
    return this.merchantStateStore.stateMachine.getState();
  }

  phoneNumberForm: FormGroup;
  otpForm: FormGroup;
  isPhoneNumberSubmitted = false;
  isOtpSent = false;
  isSendingOtp = false;
  isVerifyingOtp = false;
  otpError: string | null = null;
  phoneError: string | null = null;
  cooldownRemaining = 0;
  cooldownActive = false;
  cooldownSubscription: Subscription | null = null;
  isDebugPanelOpen = false; // Default to collapsed
  feeOption: number = 2; // Default to "Covered by you"
  settings: Settings;
  registry: Registry;
  isSkipSpinning = false;
  isTestMode: boolean;

  constructor(
    private merchantStateStore: MerchantStateStore,
    private router: Router,
    private authService: AuthService,
    private location: Location,
    private route: ActivatedRoute,
    private http: HttpClient,
    private fb: FormBuilder,
    private _registryFirebaseService: RegistryFirebaseService
  ) {
    // Set test mode once during initialization
    const hostname = window.location.hostname;
    this.isTestMode = hostname === 'localhost' || hostname.includes('kentand') || hostname === '127.0.0.1';
    // Subscribe to auth and merchant state changes
    this.authService.user.pipe(
      filter(user => user !== null && user !== undefined),
      switchMap(user => this.merchantStateStore.getMerchant(user.uid))
    ).subscribe((merchant: Merchant) => {
      if (merchant) {
        this.hasMerchant = true;
        this.merchant = merchant;
        this.merchantCountry = merchant.schemaCountry;
        this.merchantCurrency = merchant.schemaCurrency;
        // Ensure we have a valid status before transitioning
        if (merchant.status) {
          this.merchantStateStore.stateMachine.transition(merchant.status as MerchantEvent);
          this.stateMessage = getStateMessage(merchant.status as MerchantState);
        }
      } else {
        this.hasMerchant = false;
        this.stateMessage = getStateMessage(MerchantState.INITIAL);
      }
    });

    // Initialize forms
    this.phoneNumberForm = this.fb.group({
      countryCode: ['', Validators.required],
      phoneNumber: ['', [
        Validators.required,
        Validators.pattern('^[1-9][0-9]{7,14}$')  // 8-15 digits, must start with 1-9
      ]]
    });

    this.otpForm = this.fb.group({
      otpCode: ['', [Validators.required, Validators.pattern('[0-9]{4,10}')]]
    });
  }

  ngOnInit() {
    const merchant = this.route.snapshot.data['merchant'] as Merchant;
    this.merchant = merchant;

    this.registry = this.route.snapshot.data['registry'] as Registry;


    //add a step to hydrate merchant from firestore
    this.merchantStateStore.getMerchant(this.route.snapshot.data['user'].uid).subscribe((merchant: Merchant) => {
      this.merchant = merchant;
      if (merchant) {
        this.hasMerchant = true;
        this.merchantCountry = merchant.schemaCountry;
        this.merchantCurrency = merchant.schemaCurrency;
        this.selectedCountryCode = merchant.schemaCountry;
        this.phoneNumberForm.patchValue({ countryCode: merchant.schemaCountry });

        //initialis the state machine with merchant.status
        this.merchantStateStore.stateMachine.transition(merchant.status as MerchantEvent);
        this.stateMessage = getStateMessage(this.merchantStateStore.stateMachine.getState());
      }
    });

    this._registryFirebaseService.registryByUserId(this.route.snapshot.data['user'].uid).subscribe((registry: Registry) => {
      this.registry = registry;
    });

    // Initialize feeOption from settings
    this._registryFirebaseService.currentSettings.subscribe(settings => {
      if (settings) {
        this.settings = settings;
        this.feeOption = settings.feeOption || 2; // Default to 1 if not set
      }
    });
  }



  close() {
    this.router.navigate(['/registry/add-payment-methods']);
  }

  async saveAndUpgrade() {
    if (!this.settings) {
      this.settings = new Settings();
    }
    this.settings.feeOption = this.feeOption;
    await this._registryFirebaseService.saveSettings(this.settings);

    if (!this.registry.paid) {
      this.router.navigate(['/registry/payment'], { queryParams: { fromCreditCardSetup: true } });
    } else if (this.registry.paid) {
      this.router.navigate(['/registry/add-payment-methods/credit-card/add']);
    } else {

    }
  }

  onUpdate() {
    this.router.navigate(['./update'], { relativeTo: this.route });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.cooldownSubscription) {
      this.cooldownSubscription.unsubscribe();
    }
  }

  submitKyc() {
    this.merchantStateStore.submitKyc();
  }

  private hasValidSession(): boolean {
    if (!this.merchant) return false;

    const { personaSessionToken, personaInquiryId, personaTimestamp } = this.merchant;

    if (!personaSessionToken || !personaInquiryId || !personaTimestamp) {
      return false;
    }

    const sessionAge = Date.now() - personaTimestamp;
    const SESSION_VALIDITY = 24 * 60 * 60 * 1000; // 24 hours

    return sessionAge < SESSION_VALIDITY;
  }

  private clearSessionData(): void {
    this.merchantStateStore.updateMerchant({
      personaSessionToken: null,
      personaInquiryId: null,
      personaTimestamp: null
    }, this.merchant.userId);
  }

  private createPersonaClient(config: any): any {
    const Persona = (window as any).Persona;
    const baseConfig = {
      environmentId: this.personaConfig.environmentId,
      referenceId: this.merchant.userId,
      onReady: () => this.handlePersonaReady(this.personaClient),
      onComplete: (data) => this.handlePersonaComplete(data),
      onCancel: (data) => this.handlePersonaCancel(data),
      onError: (error) => this.handlePersonaError(error),
    };

    // For new verification
    if (!config.sessionToken && !config.inquiryId) {
      return new Persona.Client({
        ...baseConfig,
        templateId: this.personaConfig.templateId,
      });
    }

    // For resuming verification
    return new Persona.Client({
      ...baseConfig,
      sessionToken: config.sessionToken,
      inquiryId: config.inquiryId,
      // Remove templateId for resuming sessions
    });
  }
  startVerification(): void {


    if (this.hasValidSession()) {
      const { personaSessionToken, personaInquiryId } = this.merchant;
      // Don't clear session data when resuming
      this.resumeVerification(personaSessionToken!, personaInquiryId!);
    } else {
      // Only clear session data when starting fresh
      this.clearSessionData();
      this.startNewVerification();
    }
    this.showModal = false;
  }
  private startNewVerification(): void {
    this.isVerificationLoading = true;
    this.personaClient = this.createPersonaClient({});
  }

  private resumeVerification(sessionToken: string, inquiryId: string): void {
    this.isVerificationLoading = true;
    this.personaClient = this.createPersonaClient({ sessionToken, inquiryId });
  }

  enableTfa() {
    // We don't transition immediately - only after OTP verification is successful
    this.isPhoneNumberSubmitted = true;
  }

  async sendOtp() {

    if (this.phoneNumberForm.invalid || this.isSendingOtp) {
      return;
    }

    this.isSendingOtp = true;
    this.phoneError = null;

    try {

      const idToken: string = await this.authService.authToken().pipe(take(1)).toPromise();



      const countryCode = this.phoneNumberForm.get('countryCode').value;
      const phoneNumber = this.phoneNumberForm.get('phoneNumber').value;
      const dialCode = this.getCountryDialCode(countryCode);
      const fullPhoneNumber = `${dialCode}${phoneNumber}`;

      await this.http.post(`${environment.firebase.clouldFunctionHost}/sendOtp`, {
        phoneNumber: fullPhoneNumber,
        merchantId: this.merchant.merchantId || this.merchant.userId,
        idToken
      }).toPromise();

      this.isPhoneNumberSubmitted = true;
      this.isOtpSent = true;
      this.startCooldown();
    } catch (error) {
      this.phoneError = error.error && error.error.message
        ? error.error.message
        : 'Failed to send verification code. Please try again.';
      // Don't reset isPhoneNumberSubmitted here to maintain the form state
    } finally {
      this.isSendingOtp = false;
    }
  }

  async resendOtp() {
    if (this.cooldownActive) {
      return;
    }

    this.isSendingOtp = true;
    this.otpError = null;

    try {
      // Get idToken from firebase auth

      const idToken: string = await this.authService.authToken().pipe(take(1)).toPromise();

      const response = await this.http.post<any>(
        `${environment.firebase.clouldFunctionHost}/resendOtp`,
        {
          merchantId: this.merchant && this.merchant.merchantId ? this.merchant.merchantId : this.merchant.userId,
          idToken
        }
      ).toPromise();

      this.startCooldown();

    } catch (error) {
      console.error('Error resending OTP:', error);

      if (error.status === 429) {
        // Rate limit exceeded
        if (error.error && error.error.cooldown) {
          this.startCooldown(error.error.cooldown);
        }
        this.otpError = error.error.message || 'Too many attempts. Please try again later.';
      } else {
        this.otpError = 'Error resending verification code. Please try again.';
      }
    } finally {
      this.isSendingOtp = false;
    }
  }

  async verifyOtp() {
    if (this.otpForm.invalid) {
      this.otpError = 'Please enter a valid verification code';
      return;
    }

    this.otpError = null;
    this.isVerifyingOtp = true;

    try {
      // Get idToken from firebase auth
      const user = await this.authService.user.pipe(take(1)).toPromise();
      const idToken: string = await this.authService.authToken().pipe(take(1)).toPromise();
      const code = this.otpForm.get('otpCode').value;

      const response = await this.http.post<any>(
        `${environment.firebase.clouldFunctionHost}/verifyOtp`,
        {
          code,
          merchantId: this.merchant && this.merchant.merchantId ? this.merchant.merchantId : this.merchant.userId,
          idToken
        }
      ).toPromise();

      // OTP verified successfully, update the state
      this.merchantStateStore.stateMachine.transition(MerchantEvent.ENABLE_TFA);
      this.merchantStateStore.updateMerchant({
        status: MerchantState.TFA_ENABLED,
      }, this.merchant.userId);

      // Reset forms and state
      this.isPhoneNumberSubmitted = false;
      this.isOtpSent = false;
      this.phoneNumberForm.reset();
      this.otpForm.reset();

    } catch (error) {
      console.error('Error verifying OTP:', error);

      if (error.status === 400) {
        this.otpError = 'Invalid verification code. Please try again.';
      } else {
        this.otpError = 'Error verifying code. Please try again.';
      }
    } finally {
      this.isVerifyingOtp = false;
    }
  }

  startCooldown(seconds = 60) {
    this.cooldownActive = true;
    this.cooldownRemaining = seconds;

    if (this.cooldownSubscription) {
      this.cooldownSubscription.unsubscribe();
    }

    this.cooldownSubscription = timer(0, 1000).subscribe(() => {
      this.cooldownRemaining--;

      if (this.cooldownRemaining <= 0) {
        this.cooldownActive = false;
        if (this.cooldownSubscription) {
          this.cooldownSubscription.unsubscribe();
          this.cooldownSubscription = null;
        }
      }
    });
  }

  private handlePersonaReady(client: any): void {
    this.isVerificationLoading = false;
    this.isVerificationReady = true;
    client.open();
  }

  private handlePersonaComplete({ inquiryId, status, fields }: { inquiryId: string; status: string; fields: any }): void {
    console.log(`Sending finished inquiry ${inquiryId} to backend`);
    this.isVerificationReady = false;
    this.showModal = true;
    this.merchantStateStore.submitKyc();
    // Clear session data only after successful completion
    this.clearSessionData();
  }

  private handlePersonaCancel({ inquiryId, sessionToken }: { inquiryId: string; sessionToken: string }): void {
    console.log('onCancel');
    this.isVerificationReady = false;
    this.showModal = true;

    // Keep the same inquiryId if it exists, otherwise use the new one
    const existingInquiryId = this.merchant.personaInquiryId;
    //udpate the merchant state machine to kyccreated
    this.merchantStateStore.stateMachine.transition(MerchantEvent.KYC_CREATE);

    this.merchantStateStore.updateMerchant({
      status: MerchantState.KYC_CREATED,
      personaSessionToken: sessionToken,
      personaInquiryId: existingInquiryId || inquiryId, // Keep existing or use new
      personaTimestamp: Date.now()
    }, this.merchant.userId);
  }

  private handlePersonaError(error: any): void {
    console.log(error);
    this.isVerificationLoading = false;
    this.isVerificationReady = false;
    this.showModal = true;
    // Clear session data only on error
    this.clearSessionData();
  }

  /**
   * Gets the country dial code based on country name
   * @param country Country name or code
   * @returns Country dial code without the + sign
   */
  getCountryDialCode(country: string): string {
    const foundCountry = this.countryDialCodes.find(c => c.code === country);
    if (!foundCountry) {
      return '+1'; // Default to US
    }
    return foundCountry.dialCode;
  }

  done() {
    this.router.navigate(['/registry']);
  }

  // Add these methods to handle phone number changes
  getSelectedCountryDialCode(): string {
    const country = this.countryDialCodes.find(c => c.code === this.phoneNumberForm.get('countryCode').value);
    return country ? country.dialCode : '';
  }

  setState(state: MerchantState) {
    this.merchantStateStore.stateMachine.setStateDebug(state);

  }

  async enableCreditCardPaymentMethod() {
    this.http.post(`${environment.firebase.clouldFunctionHost}/enableCreditCardPaymentMethod`, {
      merchantId: this.merchant.merchantId,
      idToken: await this.authService.authToken().pipe(take(1)).toPromise()
    }).toPromise()
      .then((response: any) => {


      })
      .catch((e: any) => {

      });

  }

  changePhoneNumber(): void {
    this.isOtpSent = false;
    this.otpError = null;
    this.phoneError = null;
    // Keep isPhoneNumberSubmitted true to show the change number form
  }

  cancelNumberChange(): void {
    this.isOtpSent = true;
    this.otpError = null;
    this.phoneError = null;
  }

  toggleDebugPanel(): void {
    this.isDebugPanelOpen = !this.isDebugPanelOpen;
  }

  selectFee(feeOption: number) {
    this.feeOption = feeOption;
  }

  async skipVerification() {
    if (!this.isTestMode || this.isSkipSpinning || !this.merchant) {
      return;
    }

    this.isSkipSpinning = true;

    try {
      const userId = this.merchant.userId;
      const response = await this.http.get(`${environment.firebase.clouldFunctionHost}/persona-adminTestProgressKyC`, {
        params: { userId }
      }).toPromise();

      // Update merchant state to KYC_APPROVED after successful skip
      this.merchantStateStore.stateMachine.transition(MerchantEvent.KYC_APPROVE);
      this.merchantStateStore.updateMerchant({
        status: MerchantState.KYC_APPROVED,
      }, this.merchant.userId);

      this.isSkipSpinning = false;
    } catch (error) {
      console.error('Error skipping verification:', error);
      this.isSkipSpinning = false;
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/credit-card-setup/credit-card-setup.component.ts